import React, { useState, useEffect } from 'react';

import { useDarkMode } from '../../components/darkmode/DarkModeContext';
import { useAuth } from '../../pages/auth/AuthContext';
import Footer from '../../components/footer/index';
// import { jsPDF } from 'jspdf';

import { 
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  // FunnelIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  // TruckIcon,
  CogIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  MapPinIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

// API configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

const OrderHistory = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('semua');
  const [dateFilter] = useState('semua');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const { isDarkMode } = useDarkMode();

  // Use the same user data extraction pattern as MyPackages
  const getActualUserData = () => {
    if (!user) return null;
    
    // Handle nested user structure
    if (user.user && typeof user.user === 'object') {
      if (user.user.profile && typeof user.user.profile === 'object') {
        return user.user.profile;
      }
      return user.user;
    }
    
    // Handle direct profile structure
    if (user.profile && typeof user.profile === 'object' && !user.email && !user.fullName) {
      return user.profile;
    }
    
    // Handle direct user data
    return user;
  };

  const userData = getActualUserData();
  const authToken = token || sessionStorage.getItem('auth_token') || sessionStorage.getItem('token');
  const userId = userData?._id || userData?.id || null;

  // Enhanced fetch function - same pattern as MyPackages
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      let transformedOrders = [];
      
      console.log('Fetching orders for user:', {
        userId,
        userEmail: userData?.email,
        userPhone: userData?.phone,
        isAuthenticated,
        hasToken: !!authToken,
        userData: userData
      });

      // Always try to fetch data if we have user info
      if (!userData?.email && !userData?.phone && !userId) {
        // console.log('No user identifier found');
        setError('Data user tidak lengkap. Silakan login ulang.');
        setOrders([]);
        return;
      }

      // Try authenticated endpoint first if we have token and userId
      if (authToken && userId) {
        try {
          // console.log('Trying authenticated endpoint...');
          const response = await fetch(`${API_BASE_URL}/orders/user/my-orders`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            credentials: 'include'
          });

          // console.log('My-orders response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            // console.log('My-orders API response:', result);
            
            if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
              transformedOrders = result.data.map(transformOrderData);
              // console.log('Transformed orders from my-orders:', transformedOrders);
            }
          } else {
            // console.log('Authenticated endpoint failed with status:', response.status);
            const errorText = await response.text();
            // console.log('Error response:', errorText);
          }
        } catch (authError) {
          console.error('Authenticated request failed:', authError);
        }
      }

      // Try customer endpoint if no data from authenticated endpoint or no auth
      if (transformedOrders.length === 0 && (userData?.phone || userData?.email)) {
        try {
          // console.log('Trying customer endpoint...');
          const queryParams = new URLSearchParams();
          
          if (userData.email) {
            queryParams.append('email', userData.email);
            // console.log('Using email:', userData.email);
          }
          if (userData.phone) {
            queryParams.append('phone', userData.phone);
            // console.log('Using phone:', userData.phone);
          }
          
          const customerUrl = `${API_BASE_URL}/orders/customer?${queryParams.toString()}`;
          // console.log('Customer endpoint URL:', customerUrl);

          const response = await fetch(customerUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          // console.log('Customer orders response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            // console.log('Customer orders API response:', result);
            
            // Handle different response structures
            let ordersData = [];
            if (result.success && result.data) {
              ordersData = Array.isArray(result.data) ? result.data : [result.data];
            } else if (result.orders) {
              ordersData = Array.isArray(result.orders) ? result.orders : [result.orders];
            } else if (Array.isArray(result)) {
              ordersData = result;
            }
            
            // console.log('Extracted orders data:', ordersData);
            
            if (ordersData.length > 0) {
              transformedOrders = ordersData.map(transformOrderData);
              // console.log('Transformed orders from customer endpoint:', transformedOrders);
            }
          } else {
            // console.log('Customer endpoint failed with status:', response.status);
            const errorText = await response.text();
            // console.log('Error response:', errorText);
          }
        } catch (customerError) {
          console.error('Customer endpoint failed:', customerError);
        }
      }

      // Final result handling
      if (transformedOrders.length === 0) {
        // console.log('No orders found from any endpoint');
        setError('Tidak ada riwayat pesanan ditemukan.');
        setOrders([]);
      } else {
        // console.log('Setting orders:', transformedOrders);
        setOrders(transformedOrders);
        setError(null);
      }

    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Gagal mengambil data pesanan. Silakan coba lagi.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Modified useEffect to always try fetching if we have user data
  useEffect(() => {
    // console.log('useEffect triggered:', { user, userData, isAuthenticated });
    
    // Try to fetch data if we have any user information
    if (userData?.email || userData?.phone || isAuthenticated) {
      fetchOrders();
    } else {
      setLoading(false);
      setError('Silakan login untuk melihat riwayat pesanan Anda');
      setOrders([]);
    }
  }, [user, authToken, isAuthenticated]);

  // Transform API order data to match component format
  const transformOrderData = (apiOrder) => {
    // console.log('Transforming order:', apiOrder);
    
    // Map API status to component status
    const statusMap = {
      'pending': 'pending',
      'confirmed': 'scheduled', 
      'survey_scheduled': 'scheduled',
      'survey_completed': 'scheduled',
      'installation_scheduled': 'scheduled',
      'installed': 'in_progress',
      'active': 'completed',
      'cancelled': 'cancelled',
      'suspended': 'failed'
    };

    // Map package type
    const getPackageType = (packageName = '') => {
      const name = packageName.toLowerCase();
      if (name.includes('dedicated')) return 'dedicated';
      if (name.includes('satelit') || name.includes('starlink')) return 'satelit';
      if (name.includes('metro')) return 'metro-e';
      return 'broadband';
    };

    // Create timeline from status history
    const timeline = apiOrder.statusHistory ? apiOrder.statusHistory.map(history => ({
      date: history.timestamp.split('T')[0],
      time: new Date(history.timestamp).toLocaleTimeString('id-ID', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      status: statusMap[history.status] || history.status,
      title: getStatusTitle(history.status),
      desc: history.note || getStatusDescription(history.status)
    })) : [];

    // Extract package info from different possible locations - same as MyPackages
    const packageInfo = apiOrder.packageSnapshot || apiOrder.package || apiOrder.packageInfo || {};
    const addressInfo = apiOrder.address || apiOrder.installationAddress || {};
    const customerInfo = apiOrder.customer || apiOrder.customerInfo || {};
    
    // Better price extraction with more fallbacks
    let amount = 0;
    if (packageInfo.price?.amount) {
      amount = packageInfo.price.amount;
    } else if (packageInfo.monthlyFee) {
      amount = packageInfo.monthlyFee;
    } else if (apiOrder.pricing?.packagePrice) {
      amount = apiOrder.pricing.packagePrice;
    } else if (apiOrder.pricing?.totalAmount) {
      amount = apiOrder.pricing.totalAmount;
    } else if (apiOrder.totalAmount) {
      amount = apiOrder.totalAmount;
    } else if (apiOrder.price) {
      amount = apiOrder.price;
    }

    // Better location formatting - same as MyPackages
    let location = 'Alamat tidak tersedia';
    if (addressInfo.fullAddress) {
      location = addressInfo.fullAddress;
    } else if (typeof addressInfo === 'string') {
      location = addressInfo;
    } else if (addressInfo.street || addressInfo.city || addressInfo.province) {
      const parts = [
        addressInfo.street,
        addressInfo.village,
        addressInfo.district,
        addressInfo.city,
        addressInfo.province
      ].filter(Boolean);
      location = parts.join(', ');
    } else if (apiOrder.address && typeof apiOrder.address === 'string') {
      location = apiOrder.address;
    }

    // Better package name extraction
    let packageName = 'Paket Internet';
    if (packageInfo.name) {
      packageName = packageInfo.name;
    } else if (packageInfo.title) {
      packageName = packageInfo.title;
    } else if (apiOrder.packageName) {
      packageName = apiOrder.packageName;
    } else if (packageInfo.speed) {
      packageName = `Paket ${packageInfo.speed}`;
    }

    // Customer info extraction
    let customerName = 'Unknown Customer';
    let customerPhone = '';
    
    if (customerInfo.fullName) {
      customerName = customerInfo.fullName;
    } else if (customerInfo.name) {
      customerName = customerInfo.name;
    } else if (apiOrder.customerName) {
      customerName = apiOrder.customerName;
    }
    
    if (customerInfo.phone) {
      customerPhone = customerInfo.phone;
    } else if (customerInfo.phoneNumber) {
      customerPhone = customerInfo.phoneNumber;
    } else if (apiOrder.customerPhone) {
      customerPhone = apiOrder.customerPhone;
    }

    const transformedOrder = {
      id: apiOrder.orderNumber || apiOrder._id || apiOrder.id,
      packageName: packageName,
      type: getPackageType(packageName),
      status: statusMap[apiOrder.status] || apiOrder.status,
      orderDate: apiOrder.createdAt ? apiOrder.createdAt.split('T')[0] : new Date().toISOString().split('T')[0],
      installDate: apiOrder.schedule?.installationDate ? apiOrder.schedule.installationDate.split('T')[0] : null,
      completedDate: apiOrder.schedule?.activationDate ? apiOrder.schedule.activationDate.split('T')[0] : null,
      amount: amount,
      location: location,
      customerName: customerName,
      customerPhone: customerPhone,
      technician: null, // API doesn't have technician info yet
      technicianPhone: null,
      notes: apiOrder.notes?.customer || apiOrder.notes?.internal || apiOrder.description || '',
      timeline: timeline,
      originalData: apiOrder
    };
    
    // console.log('Transformed order:', transformedOrder);
    return transformedOrder;
  };

  // Download Invoice Function
  const downloadInvoice = async (order) => {
    try {
      setDownloadingInvoice(order.id);
      
      // Try to get invoice from API first
      if (authToken) {
        try {
          const response = await fetch(`${API_BASE_URL}/orders/${order.id}/invoice`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice-${order.id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return;
          }
        } catch (apiError) {
          // console.log('API invoice download failed, generating PDF:', apiError);
        }
      }

      // Generate PDF using jsPDF if API fails
      await generateInvoicePDF(order);

    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Gagal mengunduh invoice. Silakan coba lagi.');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Generate PDF Invoice
  const generateInvoicePDF = async (order) => {
    try {
      // Dynamically import jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;

      // Company Header
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('TELNET', margin, 30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('PT. Telekomunikasi Network Indonesia', margin, 40);
      doc.text('Jl. Contoh No. 123, Jakarta', margin, 48);
      doc.text('Telp: (021) 1234-5678', margin, 56);

      // Invoice Title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('INVOICE', pageWidth - margin - 40, 30);
      
      // Invoice Details
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`No. Invoice: ${order.id}`, pageWidth - margin - 80, 45);
      doc.text(`Tanggal: ${formatDate(order.orderDate)}`, pageWidth - margin - 80, 53);
      doc.text(`Status: ${getStatusText(order.status)}`, pageWidth - margin - 80, 61);

      // Line separator
      doc.line(margin, 75, pageWidth - margin, 75);

      // Customer Information
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('BILL TO:', margin, 90);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(order.customerName, margin, 100);
      doc.text(order.customerPhone, margin, 108);
      
      // Split long address
      const maxLineLength = 50;
      const addressLines = splitTextIntoLines(order.location, maxLineLength);
      let yPos = 116;
      addressLines.forEach(line => {
        doc.text(line, margin, yPos);
        yPos += 8;
      });

      // Services Table Header
      yPos += 10;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('LAYANAN:', margin, yPos);
      yPos += 15;

      // Table headers
      doc.setFontSize(10);
      doc.text('Deskripsi', margin, yPos);
      doc.text('Qty', pageWidth - margin - 60, yPos);
      doc.text('Harga', pageWidth - margin - 30, yPos);
      
      // Table line
      yPos += 5;
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Service details
      doc.setFont(undefined, 'normal');
      doc.text(order.packageName, margin, yPos);
      doc.text('1', pageWidth - margin - 60, yPos);
      doc.text(formatCurrency(order.amount), pageWidth - margin - 60, yPos);

      // Total
      yPos += 20;
      doc.line(pageWidth - margin - 80, yPos, pageWidth - margin, yPos);
      yPos += 10;
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL:', pageWidth - margin - 50, yPos);
      doc.text(formatCurrency(order.amount), pageWidth - margin - 60, yPos);

      // Terms and conditions
      yPos += 30;
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text('Syarat dan Ketentuan:', margin, yPos);
      yPos += 8;
      doc.text('1. Pembayaran dilakukan maksimal 30 hari setelah invoice diterbitkan', margin, yPos);
      yPos += 6;
      doc.text('2. Biaya instalasi sudah termasuk dalam harga paket', margin, yPos);
      yPos += 6;
      doc.text('3. Layanan akan diaktifkan setelah pembayaran diterima', margin, yPos);

      // Footer
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 30;
      }
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('Terima kasih atas kepercayaan Anda menggunakan layanan TELNET', 
               pageWidth / 2, pageHeight - 20, { align: 'center' });

      // Save PDF
      doc.save(`Invoice-${order.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Gagal membuat PDF. Pastikan browser Anda mendukung fitur ini.');
    }
  };

  // Helper function to split text into lines
  const splitTextIntoLines = (text, maxLength) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  // Get status text for invoice
  const getStatusText = (status) => {
    const statusTexts = {
      pending: 'Menunggu Konfirmasi',
      scheduled: 'Terjadwal',
      in_progress: 'Sedang Proses',
      completed: 'Selesai',
      cancelled: 'Dibatalkan',
      failed: 'Gagal'
    };
    return statusTexts[status] || status;
  };

  const getStatusTitle = (status) => {
    const titles = {
      'pending': 'Pesanan Dibuat',
      'confirmed': 'Pesanan Dikonfirmasi',
      'survey_scheduled': 'Survey Dijadwalkan',
      'survey_completed': 'Survey Selesai',
      'installation_scheduled': 'Instalasi Dijadwalkan',
      'installed': 'Instalasi Selesai',
      'active': 'Layanan Aktif',
      'cancelled': 'Pesanan Dibatalkan',
      'suspended': 'Layanan Disuspend'
    };
    return titles[status] || status;
  };

  const getStatusDescription = (status) => {
    const descriptions = {
      'pending': 'Pesanan menunggu konfirmasi',
      'confirmed': 'Pesanan telah dikonfirmasi',
      'survey_scheduled': 'Survey lokasi dijadwalkan',
      'survey_completed': 'Survey lokasi selesai',
      'installation_scheduled': 'Instalasi dijadwalkan',
      'installed': 'Instalasi berhasil diselesaikan',
      'active': 'Layanan internet sudah aktif',
      'cancelled': 'Pesanan dibatalkan',
      'suspended': 'Layanan disuspend sementara'
    };
    return descriptions[status] || '';
  };

  // Status badge function
  const getStatusBadge = (status) => {
    const badges = {
      completed: {
        icon: <CheckCircleIcon className="h-4 w-4" />,
        text: 'Selesai',
        classes: 'bg-green-100 text-green-800 border-green-200'
      },
      in_progress: {
        icon: <CogIcon className="h-4 w-4 animate-spin" />,
        text: 'Sedang Proses',
        classes: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      scheduled: {
        icon: <CalendarDaysIcon className="h-4 w-4" />,
        text: 'Terjadwal',
        classes: 'bg-purple-100 text-purple-800 border-purple-200'
      },
      pending: {
        icon: <ClockIcon className="h-4 w-4" />,
        text: 'Pending',
        classes: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      cancelled: {
        icon: <XCircleIcon className="h-4 w-4" />,
        text: 'Dibatalkan',
        classes: 'bg-gray-100 text-gray-800 border-gray-200'
      },
      failed: {
        icon: <ExclamationTriangleIcon className="h-4 w-4" />,
        text: 'Gagal',
        classes: 'bg-red-100 text-red-800 border-red-200'
      }
    };

    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.classes}`}>
        {badge.icon}
        <span className="ml-1">{badge.text}</span>
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      broadband: '📡',
      dedicated: '🔗',
      satelit: '📡',
      'metro-e': '🌐'
    };
    return icons[type] || '📡';
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Tidak tersedia';
    }
  };

  const getFilteredOrders = () => {
    return orders.filter(order => {
      const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'semua' || order.status === statusFilter;
      
      const matchesDate = dateFilter === 'semua' || 
        (dateFilter === '30hari' && new Date(order.orderDate) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) ||
        (dateFilter === '90hari' && new Date(order.orderDate) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) ||
        (dateFilter === '1tahun' && new Date(order.orderDate) >= new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  };

  const openDetailModal = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedOrder(null);
  };

  const getTimelineStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      scheduled: 'bg-purple-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-600',
      cancelled: 'bg-gray-500',
      failed: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-400';
  };

  const refreshData = async () => {
    await fetchOrders();
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} pt-20`}>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className={`h-8 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/3`}></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md p-6 space-y-4`}>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/2`}></div>
                  <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} rounded w-3/4`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug info (remove in production)
  console.log('OrderHistory render state:', {
    loading,
    orders: orders.length,
    error,
    userData,
    isAuthenticated,
    filteredOrders: getFilteredOrders().length
  });

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? 'bg-gray-900 text-white'
          : 'bg-gray-50 text-gray-900'
      } pt-20 transition-colors duration-300`}
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
     
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 rounded-xl border ${
            isDarkMode 
              ? 'bg-yellow-500/20 border-yellow-500/50' 
              : 'bg-yellow-100 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className={`h-6 w-6 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
              <div>
                <h4 className={`font-semibold ${
                  isDarkMode ? 'text-yellow-300' : 'text-yellow-800'
                }`}>
                  {error.includes('login') ? 'Autentikasi Diperlukan' : 'Peringatan'}
                </h4>
                <p className={`text-sm ${
                  isDarkMode ? 'text-yellow-200' : 'text-yellow-700'
                }`}>
                  {error}
                </p>
                {error.includes('login') && (
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="mt-2 bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Login Sekarang
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Riwayat Pesanan
              </h1>
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Lacak semua pesanan instalasi internet Anda
              </p>
              {userData && (
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  User: {userData.fullName || userData.email}
                  {orders.length > 0 && ` (${orders.length} pesanan ditemukan)`}
                </p>
              )}
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button 
                onClick={refreshData}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                disabled={loading}
              >
                <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter - Only show if has orders */}
        {orders.length > 0 && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                placeholder="Cari berdasarkan ID, paket, atau nama..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500'
                } focus:ring-2 focus:ring-green-500/20 focus:outline-none`}
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white focus:border-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
              } focus:ring-2 focus:ring-green-500/20 focus:outline-none`}
            >
              <option value="semua">Semua Status</option>
              <option value="pending">Pending</option>
              <option value="scheduled">Terjadwal</option>
              <option value="in_progress">Sedang Proses</option>
              <option value="completed">Selesai</option>
              <option value="cancelled">Dibatalkan</option>
              <option value="failed">Gagal</option>
            </select>

            {/* Date Filter */}
            <select
              className={`px-4 py-2 rounded-lg border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white focus:border-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
              } focus:ring-2 focus:ring-green-500/20 focus:outline-none`}
            >
              <option value="semua">Semua Periode</option>
              <option value="30hari">30 Hari Terakhir</option>
              <option value="90hari">90 Hari Terakhir</option>
              <option value="1tahun">1 Tahun Terakhir</option>
            </select>
          </div>
        )}

        {/* Orders List */}
        <div className="space-y-4">
          {getFilteredOrders().map(order => (
            <div
              key={order.id}
              className={`${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                
                {/* Left Section - Order Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getTypeIcon(order.type)}</div>
                      <div>
                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {order.packageName}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ID: {order.id} • {formatDate(order.orderDate)}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <MapPinIcon className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {order.location || 'Alamat tidak tersedia'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PhoneIcon className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {order.customerName} • {order.customerPhone}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <CreditCardIcon className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(order.amount)}
                        </span>
                      </div>
                      {order.installDate && (
                        <div className="flex items-center space-x-2">
                          <CalendarDaysIcon className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Instalasi: {formatDate(order.installDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {order.notes && (
                    <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        💬 {order.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Section - Actions */}
                <div className="flex flex-col space-y-2 lg:w-48">
                  <button
                    onClick={() => openDetailModal(order)}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span>Lihat Detail</span>
                  </button>

                  <button
                    onClick={() => downloadInvoice(order)}
                    disabled={downloadingInvoice === order.id}
                    className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    <span>{downloadingInvoice === order.id ? 'Memproses...' : 'Unduh Invoice'}</span>
                  </button>

                  {order.status === 'failed' && (
                    <button className={`flex items-center justify-center space-x-2 px-4 py-2 ${
                      isDarkMode ? 'bg-blue-800 hover:bg-blue-700 text-blue-200' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                    } rounded-lg font-medium transition-colors text-sm`}>
                      <PhoneIcon className="h-4 w-4" />
                      <span>Hubungi CS</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {getFilteredOrders().length === 0 && !loading && (
          <div className="text-center py-12">
            <div className={`mx-auto h-24 w-24 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              <ClipboardDocumentListIcon className="h-24 w-24" />
            </div>
            <h3 className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
              Tidak ada pesanan ditemukan
            </h3>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {orders.length === 0 
                ? (userData?.email || userData?.phone ? 'Anda belum memiliki pesanan' : 'Silakan login untuk melihat riwayat pesanan') 
                : 'Coba ubah filter pencarian'}
            </p>
            {orders.length === 0 && (userData?.email || userData?.phone) && (
              <button
                onClick={() => window.location.href = '/produk'}
                className="mt-4 px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Buat Pesanan Baru
              </button>
            )}
            {!userData?.email && !userData?.phone && (
              <button
                onClick={() => window.location.href = '/login'}
                className="mt-4 px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Login Sekarang
              </button>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedOrder && (
          <div className="fixed inset-0 z-[2000] overflow-y-auto">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeDetailModal}
            ></div>

            {/* Modal Content */}
            <div className="relative z-[2001] flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className={`inline-block align-bottom ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full`}>
                
                {/* Modal Header */}
                <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getTypeIcon(selectedOrder.type)}</div>
                      <div>
                        <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Detail Pesanan {selectedOrder.id}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {selectedOrder.packageName}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeDetailModal}
                      className={`p-2 rounded-full hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} transition-colors`}>
                      <XCircleIcon className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    </button>
                  </div>
                </div>

                {/* Modal Content Body */}
                <div className="px-6 py-4 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Order Info */}
                    <div className="space-y-6">
                      <div>
                        <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                          Informasi Pesanan
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status:</span>
                            {getStatusBadge(selectedOrder.status)}
                          </div>
                          <div className="flex justify-between">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tanggal Pesanan:</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formatDate(selectedOrder.orderDate)}
                            </span>
                          </div>
                          {selectedOrder.installDate && (
                            <div className="flex justify-between">
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tanggal Instalasi:</span>
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {formatDate(selectedOrder.installDate)}
                              </span>
                            </div>
                          )}
                          {selectedOrder.completedDate && (
                            <div className="flex justify-between">
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tanggal Selesai:</span>
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {formatDate(selectedOrder.completedDate)}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Biaya:</span>
                            <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(selectedOrder.amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                          
                      <div>
                        <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                          Informasi Pelanggan
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nama:</span>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedOrder.customerName}
                            </p>
                          </div>
                          <div>
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Telepon:</span>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedOrder.customerPhone}
                            </p>
                          </div>
                          <div>
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lokasi:</span>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedOrder.location}
                            </p>
                          </div>
                        </div>
                      </div>

                      {selectedOrder.technician && (
                        <div>
                          <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                            Teknisi
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nama Teknisi:</span>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {selectedOrder.technician}
                              </p>
                            </div>
                            <div>
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Telepon:</span>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {selectedOrder.technicianPhone}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Right Column - Timeline */}
                    <div>
                      <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Timeline Pesanan
                      </h4>
                      <div className="space-y-4">
                        {selectedOrder.timeline && selectedOrder.timeline.length > 0 ? (
                          selectedOrder.timeline.map((item, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className={`w-3 h-3 rounded-full ${getTimelineStatusColor(item.status)} mt-2 flex-shrink-0`}></div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h5 className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {item.title}
                                  </h5>
                                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {item.time}
                                  </span>
                                </div>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {item.desc}
                                </p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {formatDate(item.date)}
                                </p>
                                {index < selectedOrder.timeline.length - 1 && (
                                  <div className={`w-px h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} ml-1 mt-2`}></div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Timeline tidak tersedia
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedOrder.notes && (
                    <div className="mt-6">
                      <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                        Catatan
                      </h4>
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {selectedOrder.notes}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => downloadInvoice(selectedOrder)}
                      disabled={downloadingInvoice === selectedOrder.id}
                      className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      <DocumentArrowDownIcon className="h-4 w-4" />
                      <span>{downloadingInvoice === selectedOrder.id ? 'Memproses...' : 'Unduh Invoice'}</span>
                    </button>
                    
                    {['failed', 'cancelled'].includes(selectedOrder.status) && (
                      <button className={`flex items-center justify-center space-x-2 px-4 py-2 ${
                        isDarkMode ? 'bg-blue-800 hover:bg-blue-700 text-blue-200' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                      } rounded-lg font-medium transition-colors text-sm`}>
                        <PhoneIcon className="h-4 w-4" />
                        <span>Hubungi Customer Service</span>
                      </button>
                    )}
                    
                    {selectedOrder.status === 'in_progress' && selectedOrder.technicianPhone && (
                      <button className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors">
                        <PhoneIcon className="h-4 w-4" />
                        <span>Hubungi Teknisi</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OrderHistory;